import { Apartment } from '@dwellet/dwellet-utils'
import {
  FormControlLabel,
  Grid,
  LinearProgress,
  Radio,
  Typography,
  withStyles,
  WithStyles
} from '@material-ui/core'
import { Theme } from '@material-ui/core/styles'
import { useState } from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import {
  formValueSelector,
  reduxForm,
  change,
  Field,
  getFormValues
} from 'redux-form'
import { updateApartmentClientInformationWithStatus } from '../../../actions/apartments'
import { UpdateApartmentClientInformationWithStatus } from '../../../actions/types';
import ErrorMessage from '../../../components/Error/ErrorMessage'
import { ReduxRadioGroup } from '../../../components/FormInput/ReduxWrappers'
import { AutocompleteOption } from '../../../types/autocomplete'
import { ReduxForm } from '../../../types/forms'
import { bindSubmitActionToPromise } from '../../../utils/forms'
import Suggest from '../Stepper/Steps/Suggest'
import Client from './ClientInfoFormFields/Client'
import { apartmentStyles } from '../../../styles'

const styles = (theme: Theme) => ({
  ...apartmentStyles(theme),
  primaryBtn: {
    backgroundColor: '#4f9b44',
    '&:hover': {
      backgroundColor: '#3f7c36'
    }
  },
  skipBtn: {
    backgroundColor: '#fff',
    color: 'rgba(0, 0, 0, 0.87)',
    border: 'none',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#fff',
      boxShadow: 'none'
    }
  }
})

interface ApartmentClientInformationFormProps
  extends WithStyles,
  WithTranslation,
  ReduxForm {
  updateApartmentClientInformation: (arg: any) => any
  updateApartmentClientInformationWithStatus: UpdateApartmentClientInformationWithStatus
  updateApartmentClientInformationToOverview: (arg: any) => any
  clientTypeState: 'business' | 'private'
  roleTypeState: 'roleRealtor' | 'roleLandlord'
  hasOtherOwnersState: string
  formValues: any
  isDisabled: boolean
  autocompleteLocation?: AutocompleteOption
  changeFormValue: (field: string, value: string) => void
  customSubmitLabel?: string
  currentStep: any
  doStepBack: any
  isMobile: any
  apartment: Apartment
}

const ApartmentClientInformationForm = ({
  updateApartmentClientInformation,
  updateApartmentClientInformationWithStatus,
  updateApartmentClientInformationToOverview,
  t,
  handleSubmit,
  submitting,
  error,
  clientTypeState,
  formValues,
  roleTypeState,
  hasOtherOwnersState,
  isDisabled,
  autocompleteLocation,
  changeFormValue,
  customSubmitLabel,
  currentStep,
  doStepBack,
  isMobile,
  classes,
  apartment
}: ApartmentClientInformationFormProps) => {
  const [isLoading, setIsLoading] = useState(false)

  return (
    <form
      onSubmit={handleSubmit(updateApartmentClientInformation)}
      style={{ width: '100%' }}
    >
      <Grid container>
      <Grid item lg={6} md={12} sm={12} xs={12}>
        <Grid container className={classes.largeForm}>
            <Grid item xs={12} style={{ display: 'none' }}>
              <Typography variant='h5' gutterBottom>
                {t('apartment.steps.step12heading')}
              </Typography>
            </Grid>
            <Grid item xs={12} style={{ display: 'none' }}>
              <Field
                name='mainOwner.role'
                defaultValue='roleLandlord'
                component={ReduxRadioGroup}
              >
                <FormControlLabel
                  value='roleRealtor'
                  control={<Radio disabled={isDisabled} />}
                  label={t('business.realtor')}
                />
                <FormControlLabel
                  value='roleLandlord'
                  control={<Radio disabled={isDisabled} />}
                  label={t('business.landlord')}
                />
              </Field>
            </Grid>
            {roleTypeState && (
              <>
                <Grid item xs={12}>
                  <Typography variant='h5' gutterBottom>
                    {t('apartment.steps.step12subHeading')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Field
                    name='clientType'
                    defaultValue='private'
                    component={ReduxRadioGroup}
                  >
                    <FormControlLabel
                      value='private'
                      control={<Radio disabled={isDisabled} />}
                      label={t('business.private')}
                    />
                    <FormControlLabel
                      value='business'
                      control={<Radio disabled={isDisabled} />}
                      label={t('business.business')}
                    />
                  </Field>
                </Grid>
              </>
            )}

            {clientTypeState && roleTypeState && (
              <Grid container>
                <Client
                  isBusiness={clientTypeState === 'business'}
                  isPrivate={clientTypeState === 'private'}
                  isRealtor={roleTypeState === 'roleRealtor'}
                  isLandlord={roleTypeState === 'roleLandlord'}
                  hasOtherOwners={hasOtherOwnersState}
                  t={t}
                  isDisabled={isDisabled}
                  autocompleteLocation={autocompleteLocation}
                  changeFormValue={changeFormValue}
                  classes={classes}
                  currentStep={currentStep}
                  customSubmitLabel={customSubmitLabel}
                  doStepBack={doStepBack}
                  isLoading={isLoading}
                  updateClientInfoWithStatus={
                    apartment?.client
                      ? handleSubmit((data: any) => {
                        setIsLoading(true)
                        updateApartmentClientInformation({
                          ...formValues,
                          apartment
                        })
                      })
                      : handleSubmit((data: any) => {
                        setIsLoading(true)
                        updateApartmentClientInformationWithStatus({
                          ...formValues,
                          apartment
                        })
                      })
                  }
                  // client info update without status for rentapp
                  updateClientInfo={
                    handleSubmit((data: any) => {
                      setIsLoading(true)
                      updateApartmentClientInformation({
                        ...formValues,
                        apartment
                      })
                    })
                  }
                  updateClientInfoToOverview={
                    handleSubmit((data: any) => {
                      updateApartmentClientInformationToOverview({
                        ...formValues,
                        apartment
                      })
                    })
                  }
                  updateClientInfoToOverviewEditMode={
                    handleSubmit((data: any) => {
                      updateApartmentClientInformationToOverview({
                        ...formValues,
                        apartment,
                        editMode: true
                      })
                    })
                  }
                  apartment={apartment}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      <Grid item lg={6} md={12} sm={12} xs={12}>
        <div className={classes.infoBoxWrapper}>
            <Suggest
              fullWidth
              content={t('apartment.hints.step12Hint')}
              isModal
            />
          </div>
        </Grid>
        <ErrorMessage error={error} />
        {submitting && <LinearProgress />}
      </Grid>
    </form>
  )
}

const selector = formValueSelector('ApartmentClientInformationForm')

const mapStateToProps = (state: { [key: string]: any }) => ({
  clientTypeState: selector(state, 'clientType'),
  roleTypeState: selector(state, 'mainOwner.role'),
  hasOtherOwnersState: selector(state, 'hasOtherOwners'),
  autocompleteLocation: selector(state, 'mainOwner.autocompleteLocation'),
  formValues: getFormValues('ApartmentClientInformationForm')(state)
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  changeFormValue: (field: string, value: string) =>
    dispatch(
      change('ApartmentClientInformationForm', `mainOwner.${field}`, value)
    ),
  updateApartmentClientInformationWithStatus: bindSubmitActionToPromise(
    dispatch,
    updateApartmentClientInformationWithStatus
  )
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: 'ApartmentClientInformationForm',
    enableReinitialize: true
  })(
    withStyles(styles)(
      withTranslation()(ApartmentClientInformationForm as any)
    ) as any
  ) as any
) as any
